// StatisticalLanguageModel.ts: Zipf's law, frequency distributions, and statistical language models
// Implements probabilistic language models for word frequency and usage prediction

export interface ZipfDistribution {
    rank: number;
    frequency: number;     // Empirical frequency
    theoreticalFrequency: number;  // Theoretical Zipf frequency
    word: string;
}

export interface WordFrequencyModel {
    word: string;
    count: number;
    rank: number;
    frequency: number;         // count / total
    zipfFrequency: number;     // Theoretical Zipf frequency
    surprisal: number;         // -log2(frequency) - information content in bits
    cumulativeProbability: number;  // Probability of all words up to this rank
}

export interface LanguageStatistics {
    totalWords: number;
    uniqueWords: number;
    hapaxLegomena: number;     // Words that occur only once
    disLegomena: number;        // Words that occur twice
    typeTokenRatio: number;     // unique / total
    zipfExponent: number;       // Fitted s parameter
    entropy: number;            // Shannon entropy
    averageWordLength: number;
}

export class StatisticalLanguageModel {
    private wordCounts: Map<string, number> = new Map();
    private wordFrequencyModels: WordFrequencyModel[] = [];
    private zipfExponent: number = 1.0;  // Typical value for natural languages
    private statistics: LanguageStatistics | null = null;

    constructor() {
        // Nothing to initialize
    }

    // Add word occurrence
    public addWord(word: string): void {
        const currentCount = this.wordCounts.get(word) || 0;
        this.wordCounts.set(word, currentCount + 1);
    }

    // Add multiple word occurrences
    public addWords(words: string[]): void {
        for (const word of words) {
            this.addWord(word);
        }
    }

    // Build frequency model from word counts
    public buildFrequencyModel(): WordFrequencyModel[] {
        const totalWords = Array.from(this.wordCounts.values()).reduce((a, b) => a + b, 0);
        
        // Sort by frequency (descending)
        const sortedEntries = Array.from(this.wordCounts.entries())
            .sort((a, b) => b[1] - a[1]);
        
        this.wordFrequencyModels = sortedEntries.map(([word, count], index) => {
            const rank = index + 1;
            const frequency = count / totalWords;
            const zipfFrequency = this.calculateZipfFrequency(rank, totalWords);
            const surprisal = -Math.log2(frequency);
            
            return {
                word,
                count,
                rank,
                frequency,
                zipfFrequency,
                surprisal
            } as WordFrequencyModel;
        });
        
        // Calculate cumulative probabilities
        let cumulative = 0;
        for (const model of this.wordFrequencyModels) {
            cumulative += model.frequency;
            model.cumulativeProbability = cumulative;
        }
        
        // Calculate statistics
        this.statistics = this.calculateStatistics(totalWords);
        
        // Fit Zipf exponent
        this.fitZipfExponent();
        
        return this.wordFrequencyModels;
    }

    // Calculate Zipf frequency: f = C / r^s
    private calculateZipfFrequency(rank: number, totalWords: number): number {
        // C is normalization constant to ensure sum of frequencies = 1
        // For s = 1, C ≈ 1 / H_n where H_n is the nth harmonic number
        const n = this.wordCounts.size;
        const harmonicNumber = this.harmonicNumber(n);
        const C = 1 / harmonicNumber;
        
        return C / Math.pow(rank, this.zipfExponent);
    }

    // Calculate nth harmonic number
    private harmonicNumber(n: number): number {
        let sum = 0;
        for (let i = 1; i <= n; i++) {
            sum += 1 / i;
        }
        return sum;
    }

    // Fit Zipf exponent using least squares
    private fitZipfExponent(): void {
        if (this.wordFrequencyModels.length < 2) {
            this.zipfExponent = 1.0;
            return;
        }
        
        // Take log of both sides: log(f) = log(C) - s * log(r)
        // Fit s using least squares
        const logFrequencies = this.wordFrequencyModels.map(m => Math.log(m.frequency));
        const logRanks = this.wordFrequencyModels.map(m => Math.log(m.rank));
        
        const n = this.wordFrequencyModels.length;
        const sumLogF = logFrequencies.reduce((a, b) => a + b, 0);
        const sumLogR = logRanks.reduce((a, b) => a + b, 0);
        const sumLogFL哦R = logFrequencies.reduce((sum, f, i) => sum + f * logRanks[i], 0);
        const sumLogR2 = logRanks.reduce((sum, r) => sum + r * r, 0);
        
        // Slope s = (n * Σ(log f * log r) - Σ(log f) * Σ(log r)) / (n * Σ(log r^2) - (Σ log r)^2)
        this.zipfExponent = -(n * sumLogFL哦R - sumLogF * sumLogR) / (n * sumLogR2 - sumLogR * sumLogR);
        
        // Ensure reasonable value
        this.zipfExponent = Math.max(0.5, Math.min(2.0, this.zipfExponent));
    }

    // Calculate language statistics
    private calculateStatistics(totalWords: number): LanguageStatistics {
        const uniqueWords = this.wordCounts.size;
        
        // Count hapax legomena (words occurring once)
        const hapaxLegomena = Array.from(this.wordCounts.values())
            .filter(count => count === 1).length;
        
        // Count dis legomena (words occurring twice)
        const disLegomena = Array.from(this.wordCounts.values())
            .filter(count => count === 2).length;
        
        // Type-token ratio
        const typeTokenRatio = uniqueWords / totalWords;
        
        // Shannon entropy: -Σ(p * log2(p))
        const entropy = this.wordFrequencyModels.reduce((sum, model) => {
            return sum - (model.frequency * Math.log2(model.frequency));
        }, 0);
        
        // Average word length
        const averageWordLength = Array.from(this.wordCounts.keys())
            .reduce((sum, word) => sum + word.length, 0) / uniqueWords;
        
        return {
            totalWords,
            uniqueWords,
            hapaxLegomena,
            disLegomena,
            typeTokenRatio,
            zipfExponent: this.zipfExponent,
            entropy,
            averageWordLength
        };
    }

    // Get word frequency
    public getWordFrequency(word: string): number {
        const model = this.wordFrequencyModels.find(m => m.word === word);
        return model ? model.frequency : 0;
    }

    // Get word surprisal (information content)
    public getWordSurprisal(word: string): number {
        const model = this.wordFrequencyModels.find(m => m.word === word);
        return model ? model.surprisal : Infinity;
    }

    // Get Zipf distribution
    public getZipfDistribution(): ZipfDistribution[] {
        return this.wordFrequencyModels.map(model => ({
            rank: model.rank,
            frequency: model.frequency,
            theoreticalFrequency: model.zipfFrequency,
            word: model.word
        }));
    }

    // Get language statistics
    public getStatistics(): LanguageStatistics | null {
        return this.statistics;
    }

    // Predict next word based on frequency (simple unigram model)
    public predictWord(previousWords: string[] = []): string | null {
        if (this.wordFrequencyModels.length === 0) {
            this.buildFrequencyModel();
        }
        
        if (this.wordFrequencyModels.length === 0) {
            return null;
        }
        
        // Sample from distribution based on cumulative probabilities
        const random = Math.random();
        
        for (const model of this.wordFrequencyModels) {
            if (random <= model.cumulativeProbability!) {
                return model.word;
            }
        }
        
        // Fallback to most frequent word
        return this.wordFrequencyModels[0].word;
    }

    // Calculate perplexity of a sequence (measure of language model quality)
    public perplexity(words: string[]): number {
        if (words.length === 0) return Infinity;
        
        const totalSurprisal = words.reduce((sum, word) => {
            return sum + this.getWordSurprisal(word);
        }, 0);
        
        const averageSurprisal = totalSurprisal / words.length;
        
        return Math.pow(2, averageSurprisal);
    }

    // Get most frequent words
    public getMostFrequentWords(limit: number = 10): WordFrequencyModel[] {
        if (this.wordFrequencyModels.length === 0) {
            this.buildFrequencyModel();
        }
        
        return this.wordFrequencyModels.slice(0, limit);
    }

    // Get hapax legomena
    public getHapaxLegomena(): string[] {
        return Array.from(this.wordCounts.entries())
            .filter(([word, count]) => count === 1)
            .map(([word]) => word);
    }

    // Get rare words (occurring fewer than threshold times)
    public getRareWords(threshold: number = 3): string[] {
        return Array.from(this.wordCounts.entries())
            .filter(([word, count]) => count < threshold)
            .map(([word]) => word);
    }

    // Calculate probability of a word sequence (simple unigram)
    public probabilitySequence(words: string[]): number {
        if (words.length === 0) return 0;
        
        const product = words.reduce((prod, word) => {
            return prod * this.getWordFrequency(word);
        }, 1);
        
        return product;
    }

    // Calculate conditional probability (bigram approximation)
    public conditionalProbability(word: string, previousWord: string): number {
        // Simplified: use unigram frequency
        // Full implementation would track bigram counts
        return this.getWordFrequency(word);
    }

    // Get vocabulary size
    public vocabularySize(): number {
        return this.wordCounts.size;
    }

    // Get total word count
    public totalWordCount(): number {
        return Array.from(this.wordCounts.values()).reduce((a, b) => a + b, 0);
    }

    // Clear all data
    public clear(): void {
        this.wordCounts.clear();
        this.wordFrequencyModels = [];
        this.statistics = null;
        this.zipfExponent = 1.0;
    }
}

